import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";
import { FiAlertTriangle } from "react-icons/fi";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import Button from "@/components/loading-button";
import useStore from "@/services/store";
import api from "@/services/api";

const translations = {
  en: {
    description: "Define your culture profile through dilemmas. Used for matching, coaching, and your career page.",
    results_title: "Your Culture Profile",
    radar_title: "Culture Radar",
    career_text_title: "Career Page Text",
    career_text_subtitle: "Generated for your career page. You can edit it.",
    save_text: "Save Text",
    text_saved: "Career text saved",
    axes_title: "Axis Details",
    confidence: "Confidence",
    inconsistency: "Inconsistency detected",
    trade_off: "Trade-off",
    low_confidence_note: "Low confidence axes are excluded from candidate matching",
  },
  fr: {
    description: "Definissez votre profil culturel a travers des dilemmes. Utilise pour le matching, le coaching et votre page carriere.",
    results_title: "Votre profil culturel",
    radar_title: "Radar culturel",
    career_text_title: "Texte page carriere",
    career_text_subtitle: "Genere pour votre page carriere. Vous pouvez le modifier.",
    save_text: "Enregistrer le texte",
    text_saved: "Texte sauvegarde",
    axes_title: "Detail des axes",
    confidence: "Confiance",
    inconsistency: "Incoherence detectee",
    trade_off: "Compromis",
    low_confidence_note: "Les axes a faible confiance sont exclus du matching candidat",
  },
};

export default function CultureResult() {
  const { t } = useComponentTranslation(translations);
  const { organization, setOrganization } = useStore();

  const [careerText, setCareerText] = useState(organization?.career_text || "");
  const [savingText, setSavingText] = useState(false);

  const profile = organization?.culture_profile;
  const radarData = (profile?.axes || []).map((a) => ({
    axis: a.axis_label,
    value: a.score,
    fullMark: 10,
  }));

  const handleSaveCareerText = async () => {
    setSavingText(true);
    try {
      const { ok, data } = await api.put("/organization", { organization_id: organization._id, career_text: careerText });
      if (!ok) throw new Error("Failed to save");
      setOrganization(data);
      toast.success(t("text_saved"));
    } catch (e) {
      console.error(e);
      toast.error(e.message);
    } finally {
      setSavingText(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{t("results_title")}</h1>
      <p className="text-gray-500 mb-8">{t("description")}</p>

      <div className="max-w-4xl space-y-8">
        {/* Radar Chart */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t("radar_title")}</h2>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="axis" tick={{ fill: "#6b7280", fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: "#9ca3af", fontSize: 10 }} />
                <Radar name="Culture" dataKey="value" stroke="#6C4CFF" fill="#6C4CFF" fillOpacity={0.2} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Axis Details */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t("axes_title")}</h2>
          <div className="space-y-4">
            {(profile?.axes || []).map((axis) => (
              <div key={axis.axis} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{axis.axis_label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${axis.confidence === "high" ? "bg-green-100 text-green-700" : axis.confidence === "medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    {t("confidence")}: {axis.confidence}
                  </span>
                </div>

                {/* Score bar */}
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs text-gray-500 w-20 text-right">{axis.label_a}</span>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full relative">
                    <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full shadow-sm" style={{ left: `${(axis.score / 10) * 100}%`, transform: "translate(-50%, -50%)" }} />
                  </div>
                  <span className="text-xs text-gray-500 w-20">{axis.label_b}</span>
                </div>

                <p className="text-sm text-gray-600">{axis.trade_off}</p>

                {axis.inconsistency && (
                  <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 rounded-lg">
                    <FiAlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">{t("inconsistency")}: {axis.inconsistency}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          {(profile?.axes || []).some((a) => a.confidence === "low") && <p className="text-xs text-gray-400 mt-4">{t("low_confidence_note")}</p>}
        </div>

        {/* Career Text */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">{t("career_text_title")}</h2>
          <p className="text-sm text-gray-500 mb-4">{t("career_text_subtitle")}</p>
          <textarea value={careerText} onChange={(e) => setCareerText(e.target.value)} className="input min-h-[200px] mb-4" rows={8} />
          <div className="flex justify-end">
            <Button loading={savingText} onClick={handleSaveCareerText} className="btn btn-primary">
              {t("save_text")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
